import { Plus, Users } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { Button } from '../../../components/atoms/button/button';
import { EmptyState } from '../../../components/molecules/empty-state/empty-state';
import { ErrorState } from '../../../components/molecules/error-state/error-state';
import { Pagination } from '../../../components/molecules/pagination/pagination';
import { useAuth } from '../../../hooks/use-auth';
import { useUsuarios } from '../../../hooks/use-usuarios';
import {
  activateUsuarioThunk,
  createUsuarioThunk,
  deactivateUsuarioThunk,
  updatePermissionsThunk,
  updateUsuarioThunk,
} from '../../../store/slices/usuarios.slice';
import { ActivateUserModal } from './components/activate-user-modal';
import { CreateUserModal } from './components/create-user-modal';
import { DeactivateUserModal } from './components/deactivate-user-modal';
import { EditPermissionsModal } from './components/edit-permissions-modal';
import { EditUserModal } from './components/edit-user-modal';
import { UserDetailDrawer } from './components/user-detail-drawer';
import { UsuariosCards } from './components/usuarios-cards';
import { UsuariosFiltersBar } from './components/usuarios-filters';
import { UsuariosTable } from './components/usuarios-table';
import {
  CardsWrapper,
  Header,
  HeaderText,
  Page,
  SkeletonRow,
  Subtitle,
  TableWrapper,
  Title,
} from './usuarios.styles';

export const UsuariosPage = () => {
  const { user: currentUser, hasPermission } = useAuth();
  const {
    items,
    page,
    limit,
    total,
    totalPages,
    filters,
    status,
    error,
    setFilters,
    clearFilters,
    setPage,
    setLimit,
    refresh,
    selectedUserId,
    selectedDetail,
    detailStatus,
    detailError,
    openDrawer,
    closeDrawer,
    create,
    update,
    activate,
    deactivate,
    updatePermissions,
    permissions,
    mutating,
    mutationError,
    clearMutationError,
  } = useUsuarios();

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editPermsOpen, setEditPermsOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);

  const canCreate = hasPermission('USER_CREATE');
  const canUpdate = hasPermission('USER_UPDATE');
  const canActivate = hasPermission('USER_ACTIVATE');
  const canDeactivate = hasPermission('USER_DEACTIVATE');
  const canEditPermissions = hasPermission('USER_PERMISSION_UPDATE');

  const isSelf = selectedDetail?.id === currentUser?.id;
  const isLoading = status === 'loading';
  const hasError = status === 'error';
  const isEmpty = !isLoading && !hasError && items.length === 0;

  const handleCreate = async (payload: Parameters<typeof create>[0]) => {
    const result = await create(payload);
    if (createUsuarioThunk.fulfilled.match(result)) {
      toast.success('Usuário criado');
      setCreateOpen(false);
    }
  };

  const handleEdit = async (payload: Parameters<typeof update>[1]) => {
    if (!selectedDetail) return;
    const result = await update(selectedDetail.id, payload);
    if (updateUsuarioThunk.fulfilled.match(result)) {
      toast.success('Dados atualizados');
      setEditOpen(false);
    }
  };

  const handleSavePermissions = async (keys: string[]) => {
    if (!selectedDetail) return;
    const result = await updatePermissions(selectedDetail.id, {
      permissions: keys,
    });
    if (updatePermissionsThunk.fulfilled.match(result)) {
      toast.success('Permissões atualizadas');
      setEditPermsOpen(false);
    }
  };

  const handleActivate = async () => {
    if (!selectedDetail) return;
    const result = await activate(selectedDetail.id);
    if (activateUsuarioThunk.fulfilled.match(result)) {
      toast.success('Usuário ativado');
      setActivateOpen(false);
    }
  };

  const handleDeactivate = async () => {
    if (!selectedDetail) return;
    const result = await deactivate(selectedDetail.id);
    if (deactivateUsuarioThunk.fulfilled.match(result)) {
      toast.success('Usuário desativado');
      setDeactivateOpen(false);
    }
  };

  return (
    <Page>
      <Header>
        <HeaderText>
          <Title>Usuários internos</Title>
          <Subtitle>
            Gerencie acesso ao backoffice — crie, ative/desative e ajuste
            permissões dos usuários internos da gestão.
          </Subtitle>
        </HeaderText>
        {canCreate && (
          <Button
            variant="primary"
            startIcon={<Plus size={14} />}
            onClick={() => {
              clearMutationError();
              setCreateOpen(true);
            }}
            data-testid="open-create-user-modal"
          >
            Novo usuário
          </Button>
        )}
      </Header>

      <UsuariosFiltersBar
        initialValues={filters}
        permissions={permissions}
        onApply={setFilters}
        onClear={clearFilters}
      />

      {isLoading && (
        <div data-testid="usuarios-loading">
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {hasError && (
        <ErrorState
          title="Não foi possível carregar os usuários"
          description={error ?? 'Tente novamente em alguns instantes.'}
          action={
            <Button variant="secondary" onClick={() => refresh()}>
              Tentar novamente
            </Button>
          }
        />
      )}

      {isEmpty && (
        <EmptyState
          icon={<Users size={26} />}
          title="Nenhum usuário encontrado"
          description="Tente ajustar os filtros ou cadastre um novo usuário interno."
          action={
            canCreate ? (
              <Button
                variant="primary"
                startIcon={<Plus size={14} />}
                onClick={() => setCreateOpen(true)}
              >
                Novo usuário
              </Button>
            ) : undefined
          }
        />
      )}

      {!isLoading && !hasError && items.length > 0 && (
        <>
          <TableWrapper>
            <UsuariosTable items={items} onOpenDetail={openDrawer} />
          </TableWrapper>
          <CardsWrapper>
            <UsuariosCards items={items} onOpenDetail={openDrawer} />
          </CardsWrapper>
          <Pagination
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </>
      )}

      <UserDetailDrawer
        isOpen={selectedUserId !== null}
        detail={selectedDetail}
        status={detailStatus}
        errorMessage={detailError}
        isSelf={isSelf}
        canUpdate={canUpdate}
        canActivate={canActivate}
        canDeactivate={canDeactivate}
        canEditPermissions={canEditPermissions}
        onClose={() => {
          if (mutating) return;
          closeDrawer();
          clearMutationError();
        }}
        onEdit={() => {
          clearMutationError();
          setEditOpen(true);
        }}
        onEditPermissions={() => {
          clearMutationError();
          setEditPermsOpen(true);
        }}
        onActivate={() => {
          clearMutationError();
          setActivateOpen(true);
        }}
        onDeactivate={() => {
          clearMutationError();
          setDeactivateOpen(true);
        }}
      />

      <CreateUserModal
        isOpen={createOpen}
        permissions={permissions}
        loading={mutating?.action === 'create'}
        errorMessage={mutating?.action === 'create' ? null : mutationError}
        onConfirm={handleCreate}
        onClose={() => {
          if (mutating?.action === 'create') return;
          setCreateOpen(false);
          clearMutationError();
        }}
      />

      <EditUserModal
        isOpen={editOpen}
        user={selectedDetail}
        loading={mutating?.action === 'update'}
        errorMessage={mutating?.action === 'update' ? null : mutationError}
        onConfirm={handleEdit}
        onClose={() => {
          if (mutating?.action === 'update') return;
          setEditOpen(false);
          clearMutationError();
        }}
      />

      <EditPermissionsModal
        isOpen={editPermsOpen}
        user={selectedDetail}
        permissions={permissions}
        loading={mutating?.action === 'permissions'}
        errorMessage={mutating?.action === 'permissions' ? null : mutationError}
        onConfirm={handleSavePermissions}
        onClose={() => {
          if (mutating?.action === 'permissions') return;
          setEditPermsOpen(false);
          clearMutationError();
        }}
      />

      <DeactivateUserModal
        isOpen={deactivateOpen}
        user={selectedDetail}
        loading={mutating?.action === 'deactivate'}
        errorMessage={mutating?.action === 'deactivate' ? null : mutationError}
        onConfirm={handleDeactivate}
        onClose={() => {
          if (mutating?.action === 'deactivate') return;
          setDeactivateOpen(false);
          clearMutationError();
        }}
      />

      <ActivateUserModal
        isOpen={activateOpen}
        user={selectedDetail}
        loading={mutating?.action === 'activate'}
        errorMessage={mutating?.action === 'activate' ? null : mutationError}
        onConfirm={handleActivate}
        onClose={() => {
          if (mutating?.action === 'activate') return;
          setActivateOpen(false);
          clearMutationError();
        }}
      />
    </Page>
  );
};
